"use client";

import { useState } from "react";
import type { SupplierEvaluationProject, EvaluatorUser } from "../types";
import { INDUSTRIES } from "../types";
import { updateEvaluation } from "@/platform/firebase/firestore/supplier-evaluation";

interface Props {
  project: SupplierEvaluationProject;
  onNext: () => void;
  onProjectUpdate: (p: SupplierEvaluationProject) => void;
  isOwner: boolean;
  onAddEvaluator: (email: string) => Promise<void>;
  onRemoveEvaluator: (ev: EvaluatorUser) => Promise<void>;
}

export function Step1Overview({ project, onNext, onProjectUpdate, isOwner, onAddEvaluator, onRemoveEvaluator }: Props) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const isKnown = INDUSTRIES.some((i) => i.value === project.supplier_info.industry);
  const [fields, setFields] = useState({
    company_name:   project.supplier_info.company_name,
    contact_person: project.supplier_info.contact_person,
    email:          project.supplier_info.email,
    industry:       isKnown ? project.supplier_info.industry : "other",
    customIndustry: isKnown ? "" : project.supplier_info.industry,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const finalIndustry = fields.industry === "other" ? fields.customIndustry : fields.industry;
      const updatedInfo = { ...project.supplier_info, ...fields, industry: finalIndustry };
      await updateEvaluation(project.evaluation_id, {
        supplier_info: updatedInfo,
        project_name: `${fields.company_name} Evaluation`,
      });
      onProjectUpdate({ ...project, supplier_info: updatedInfo, project_name: `${fields.company_name} Evaluation` });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleAddEvaluator = async () => {
    const email = inviteEmail.trim();
    if (!email.includes("@")) return;
    setInviting(true);
    try {
      await onAddEvaluator(email);
      setInviteEmail("");
    } finally {
      setInviting(false);
    }
  };

  const industryLabel = INDUSTRIES.find((i) => i.value === project.supplier_info.industry)?.label
    ?? project.supplier_info.industry;

  const rows: [string, string][] = [
    ["Company",  project.supplier_info.company_name],
    ["Contact",  project.supplier_info.contact_person],
    ["Email",    project.supplier_info.email],
    ["Industry", industryLabel],
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Supplier info card */}
      <div className="diag-card" style={{ gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 600 }}>Supplier Information</h3>
          {!editing ? (
            <button onClick={() => setEditing(true)} style={ghostBtnStyle}>Edit</button>
          ) : (
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={() => setEditing(false)} disabled={saving} style={ghostBtnStyle}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={primaryBtnStyle}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {(["company_name", "contact_person", "email"] as const).map((key) => (
              <div key={key} style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "8px", alignItems: "center" }}>
                <label style={labelStyle}>{key === "company_name" ? "Company" : key === "contact_person" ? "Contact" : "Email"}</label>
                <input
                  type={key === "email" ? "email" : "text"}
                  value={fields[key]}
                  onChange={(e) => setFields({ ...fields, [key]: e.target.value })}
                  style={inputStyle}
                />
              </div>
            ))}
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "8px", alignItems: "center" }}>
              <label style={labelStyle}>Industry</label>
              <select value={fields.industry} onChange={(e) => setFields({ ...fields, industry: e.target.value })} style={inputStyle}>
                {INDUSTRIES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
            </div>
            {fields.industry === "other" && (
              <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "8px", alignItems: "center" }}>
                <span style={labelStyle} />
                <input
                  type="text"
                  placeholder="Enter industry name"
                  value={fields.customIndustry}
                  onChange={(e) => setFields({ ...fields, customIndustry: e.target.value })}
                  style={inputStyle}
                />
              </div>
            )}
          </div>
        ) : (
          <div>
            {rows.map(([label, value]) => (
              <div key={label} style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "8px", padding: "8px 0", borderBottom: "1px solid var(--line)" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--ink-soft)" }}>{label}</span>
                <span style={{ fontSize: "13px", color: "var(--ink)" }}>{value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Evaluation focus card */}
      <div className="diag-card" style={{ gap: "12px" }}>
        <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 600 }}>Evaluation Focus</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {project.evaluation_focus.map((f) => (
            <span key={f} style={tagStyle}>{f}</span>
          ))}
        </div>
        {project.additional_context && (
          <p style={{ fontSize: "13px", color: "var(--ink-mute)", marginTop: "4px" }}>{project.additional_context}</p>
        )}
      </div>

      {/* My Team (owner only) */}
      {isOwner && (
        <div className="diag-card" style={{ gap: "12px" }}>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 600 }}>My Team</h3>
          <p style={{ fontSize: "13px", color: "var(--ink-mute)", marginTop: "-6px" }}>Invite team members to help rate this evaluation</p>
          {(project.evaluator_users ?? []).length === 0 ? (
            <p style={{ fontSize: "13px", color: "var(--ink-mute)", textAlign: "center", padding: "12px 0" }}>No team members added yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {(project.evaluator_users ?? []).map((u) => (
                <div key={u.uid} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 4px" }}>
                  <span style={{ width: "28px", height: "28px", borderRadius: "50%", background: "rgba(91,108,255,0.15)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: 700, color: "var(--indigo)", flexShrink: 0 }}>
                    {u.email.slice(0, 2).toUpperCase()}
                  </span>
                  <span style={{ fontSize: "13px", flex: 1 }}>{u.email}</span>
                  <button onClick={() => onRemoveEvaluator(u)} style={ghostBtnStyle}>Remove</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: "8px", paddingTop: "8px", borderTop: "1px solid var(--line)" }}>
            <input
              type="email"
              placeholder="teammate@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddEvaluator()}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button onClick={handleAddEvaluator} disabled={inviting || !inviteEmail.includes("@")} style={primaryBtnStyle}>
              {inviting ? "…" : "Invite"}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={onNext} style={primaryBtnStyle}>Next: Generate Questions →</button>
      </div>
    </div>
  );
}

// Shared inline styles
const ghostBtnStyle: React.CSSProperties = {
  background: "var(--paper-2)", border: "1px solid var(--line-strong)",
  borderRadius: "8px", padding: "6px 12px", fontSize: "13px",
  color: "var(--ink-soft)", cursor: "pointer",
};
const primaryBtnStyle: React.CSSProperties = {
  background: "linear-gradient(135deg, var(--indigo), var(--magenta))",
  border: "none", borderRadius: "8px", padding: "8px 16px",
  fontSize: "13px", fontWeight: 600, color: "#fff", cursor: "pointer",
};
const inputStyle: React.CSSProperties = {
  background: "var(--paper-3)", border: "1px solid var(--line-strong)",
  borderRadius: "8px", padding: "7px 10px", fontSize: "13px",
  color: "var(--ink)", width: "100%",
};
const labelStyle: React.CSSProperties = {
  fontSize: "13px", fontWeight: 600, color: "var(--ink-soft)",
};
const tagStyle: React.CSSProperties = {
  fontSize: "11px", fontWeight: 600, padding: "3px 10px",
  borderRadius: "100px", background: "rgba(91,108,255,0.12)",
  color: "var(--accent, #3D4ECC)", border: "1px solid rgba(91,108,255,0.35)",
};
